import { Accordion, Icon } from '@scale/scaleui';
import {
  SearchResultV2,
  SearchResultAction as ISearchResultAction,
} from '@scale/shared/types/chat';
import { useState, useCallback } from 'react';
import _ from 'lodash';
import useFetchSearchResults from './useFetchSearchResults';
import SearchResultsSection from './SearchResultsSection';

interface ReferencesAccordionProps {
  setReference: (
    referenceIndex: number,
    update: {
      searchQuery: string;
      searchJustification: string;
      searchQueryResults: SearchResultV2[];
    },
  ) => void;
  deleteReference: (referenceIndex: number) => void;
  referenceIndex: number;
  existingSearchQuery: string;
  existingSearchJustification: string;
  existingSearchQueryResults: SearchResultV2[];
  readOnly: boolean;
  handleAddSearchResultAction: (index: number, action: ISearchResultAction) => void;
  handleEditSearchResultAction: (
    index: number,
    actionIndex: number,
    action: ISearchResultAction,
  ) => void;
  handleRemoveSearchResultAction: (index: number, actionIndex: number) => void;
  handleActionReorder: (index: number, sourceIndex: number, destinationIndex: number) => void;
}

const ReferencesAccordion: React.FC<ReferencesAccordionProps> = ({
  setReference,
  deleteReference,
  referenceIndex,
  existingSearchQuery,
  existingSearchJustification,
  existingSearchQueryResults,
  readOnly,
  handleAddSearchResultAction,
  handleEditSearchResultAction,
  handleRemoveSearchResultAction,
  handleActionReorder,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>(existingSearchQuery);
  const [searchJustification, setSearchQueryJustification] = useState(existingSearchJustification);
  const [expanded, setExpanded] = useState<boolean>(
    !_.some(existingSearchQueryResults, ['isChecked', true]),
  );

  const processSearchResults = useCallback(
    (searchTerm: string, searchJustification: string, searchResults: SearchResultV2[]) => {
      const newSearchResults = searchResults.map(result => ({
        ...result,
        isChecked: false,
      }));
      setReference(referenceIndex, {
        searchQuery: searchTerm,
        searchJustification,
        searchQueryResults: newSearchResults,
      });
    },
    [referenceIndex, setReference],
  );
  const [searchState, fetchSearchResults] = useFetchSearchResults(processSearchResults);

  return (
    <div className="flex w-full">
      <div className="flex-grow">
        <Accordion
          header={searchQuery.length > 0 ? searchQuery : `Reference ${referenceIndex + 1}`}
          onChange={() => {
            setExpanded(!expanded);
          }}
          expanded={expanded}
          target={
            <div className="space-y-4">
              <SearchResultsSection
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchJustification={searchJustification}
                setSearchQueryJustification={setSearchQueryJustification}
                readOnly={!!readOnly}
                searchQueryResults={existingSearchQueryResults}
                disableFetchSearchResults={!!readOnly}
                searchState={searchState}
                fetchSearchResults={fetchSearchResults}
                onCheckboxChange={(index: number) => {
                  const updatedResults = _.cloneDeep(existingSearchQueryResults);
                  updatedResults[index].isChecked = !updatedResults[index].isChecked;
                  updatedResults[index].checkedAt = Date.now();
                  setReference(referenceIndex, {
                    searchQuery,
                    searchJustification,
                    searchQueryResults: updatedResults,
                  });
                }}
                resultIndex={referenceIndex + 2}
                handleAddSearchResultAction={handleAddSearchResultAction}
                handleEditSearchResultAction={handleEditSearchResultAction}
                handleRemoveSearchResultAction={handleRemoveSearchResultAction}
                handleActionReorder={handleActionReorder}
              />
            </div>
          }
        />
      </div>
      <div className="flex-shrink-0 flex justify-center items-center ml-2">
        <Icon
          icon={['far', 'trash-alt']}
          size="md"
          variant="neutral"
          onClick={() => {
            deleteReference(referenceIndex);
          }}
          disabled={readOnly}
        />
      </div>
    </div>
  );
};

export default ReferencesAccordion;
